<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Manage LO Assignment</h1>
        <div class="header-controls">
          <div class="congress-selector">
            <label>Congress:</label>
            <Select v-model="selectedCongress" :options="congressOptions" optionLabel="label" optionValue="value" @change="loadData" />
          </div>
        </div>
      </div>

      <div class="chamber-tabs">
        <Button :label="`Senate (${senateCommittees.length})`" :severity="selectedChamber === 'Senate' ? 'primary' : 'secondary'" @click="selectedChamber = 'Senate'" />
        <Button :label="`House (${houseCommittees.length})`" :severity="selectedChamber === 'House' ? 'primary' : 'secondary'" @click="selectedChamber = 'House'" />
      </div>

      <DataTable
        :value="filteredCommittees"
        :paginator="true"
        :rows="15"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <h3>{{ selectedChamber }} Committees</h3>
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="name" header="Committee" sortable />
        <Column header="Liaison Officer/s" style="min-width: 300px">
          <template #body="{ data }">
            <MultiSelect
              v-model="data.lo_user_ids"
              :options="userOptions"
              optionLabel="name"
              optionValue="id"
              placeholder="Select LO/s"
              class="w-full"
              filter
              display="chip"
              @change="saveLOAssignment(data)"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const selectedCongress = ref('')
const selectedChamber = ref('Senate')
const committees = ref([])
const userOptions = ref([])
const loading = ref(false)

const filters = ref({
  global: { value: null, matchMode: 'contains' }
})

const congressOptions = [
  { label: '20th Congress', value: '20' },
  { label: '19th Congress', value: '19' },
  { label: '18th Congress', value: '18' },
  { label: '17th Congress', value: '17' }
]

const senateCommittees = computed(() => committees.value.filter(c => c.chamber === 'Senate'))
const houseCommittees = computed(() => committees.value.filter(c => c.chamber === 'House'))
const filteredCommittees = computed(() => committees.value.filter(c => c.chamber === selectedChamber.value))

const loadData = async () => {
  try {
    loading.value = true
    const [assignmentRes, userRes] = await Promise.all([
      api.get(`/lo-assignments?congress_no=${selectedCongress.value}`),
      api.get('/lo-officers')
    ])
    committees.value = assignmentRes.data.map(c => ({
      ...c,
      lo_user_ids: (c.liaison_officers || []).map(lo => lo.id)
    }))
    userOptions.value = userRes.data
  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
}

const saveLOAssignment = async (committee) => {
  try {
    await api.put(`/committees/${committee.id}/assign-lo`, {
      user_ids: committee.lo_user_ids
    })
  } catch (error) {
    console.error('Error saving LO assignment:', error)
    Swal.fire({ title: 'Error!', text: 'Failed to save assignment', icon: 'error' })
  }
}

const loadCurrentCongress = async () => {
  try {
    const response = await api.get('/current-congress')
    selectedCongress.value = response.data.congress_no
  } catch (error) {
    selectedCongress.value = '20'
  }
}

onMounted(async () => {
  await loadCurrentCongress()
  loadData()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  margin: 1rem;
  padding: 1.5rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.congress-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.congress-selector label {
  font-weight: 600;
  color: #1e3a8a;
}

.chamber-tabs {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header h3 {
  color: #1e3a8a;
  margin: 0;
}

:deep(.p-datatable .p-multiselect),
:deep(.p-datatable .p-select) {
  width: 100% !important;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }

  .chamber-tabs {
    flex-direction: column;
  }
}
</style>
